/**
 * Avatar utilities for generating and working with avatars
 * Uses UI Avatars (https://ui-avatars.com/) as a fallback
 * for when users don't provide their own avatar URLs
 */

/**
 * Generate a UI Avatar URL based on the user's name
 * This provides a consistent, personalized fallback avatar
 */
export function generateAvatarUrl(name: string, size: number = 128): string {
  // Default to "User" if no name is provided
  const displayName = name?.trim() || 'User';
  
  // Generate UI Avatar URL with randomized background color
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&size=${size}&background=random`;
}

/**
 * Get avatar URL, falling back to UI Avatars if none provided
 */
export function getAvatarUrl(avatarUrl: string | null, name: string): string {
  // If avatar URL exists and is valid, use it
  if (avatarUrl && avatarUrl.trim().length > 0) {
    return avatarUrl;
  }
  
  // Otherwise generate a UI Avatar
  return generateAvatarUrl(name);
}

/**
 * Generate a UI Avatar URL specifically for seller logos
 * Using different parameters to distinguish from user avatars
 */
export function generateSellerLogoUrl(name: string, size: number = 128): string {
  // Default to "Seller" if no name is provided
  const displayName = name?.trim() || 'Seller';
  
  // Generate UI Avatar URL with business-like colors
  // Using blue background to differentiate from user avatars
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&size=${size}&background=0D8ABC&color=fff&bold=true`;
}

/**
 * Get seller logo URL, falling back to UI Avatars if none provided
 */
export function getSellerLogoUrl(logoUrl: string | null, name: string): string {
  // If logo URL exists and is valid, use it
  if (logoUrl && logoUrl.trim().length > 0) {
    return logoUrl;
  }
  
  // Otherwise generate a UI Avatar for the seller
  return generateSellerLogoUrl(name);
} 